<template lang="pug">
  #profile-page
    template(v-if="usernameNotFound")
      WtTile(child)
        WtTitle(h1) User does not exist, return to your&nbsp
          span
            RouterLink(:to="{ name: 'MyDashboard' }") dashboard.

    template(v-else)
      .level.is-mobile
        .level-left
          .level-item
            WtTitle(h1) {{ routeUsername }}'s Profile
          .level-item(v-if="routeUsername !== $auth.user().username")
            .button.is-text.is-loading(
              v-if="friendsDataLoading && !memberProfileLoaded"
            )
            WtAddFriendButton(
              ref="WtAddFriendButton"
              v-show="!_.isEmpty(member) && memberProfileLoaded && !friendsDataLoading"
              :key="routeUsername"
              :member="member"
            )
        .level-right

      WtTile(ancestor)
        WtTile(vertical :col="8")
          WtTile(:col="12")
            WtTile(parent :col="6")
              WtTile(child :col="12" :is-loading="!memberProfileLoaded")
                UserCard(:data="member" isProfile)
            WtTile(parent :col="6")
              WtTile(child :col="12" :is-loading="!memberProfileLoaded")
                TodaysProgress(:member-object="member" isProfile)
          WtTile(parent vertical :col="12")
            WtTile(child :is-loading="!stepDataProfileLoaded")
              StepsChart(
                :stepData="stepData"
                isProfile
                :memberUsername="memberUsername"
              )
            WtTile(child v-if="coreProgramMeta.allowJournals" :is-loading="!journalsLoaded")
              Journal(
                :title="`${memberUsername}'s Journal Entries`"
                :memberUsername="memberUsername"
                :memberHasEntries="_.get(member, 'memberData.numberOfEntries', 0) > 0"
                :loaded="journalsLoaded"
                :is-last-page="isJournalLastPage"
                :journal-entry-stats="journalEntryStats"
                :journal-data="journals"
                :fetch-action="fetchMemberJournals"
                isProfile
              )
        WtTile(parent vertical :col="4")
          WtTile(child :col="12" :is-loading="!challengesProfileLoaded")
            EmptyEntityPlaceholder(
              v-if="_.isEmpty(challenges)"
              title="Challenges"
              :image="require(routeUsername !== $auth.user().username ? '@/assets/placeholders/empty_woman_walking.jpg' : '@/assets/placeholders/empty_friends.jpg')"
              :callout="isPageLoaded(page) ? `${_.upperFirst(routeUsername)} is not in any challenges.` : ''"
            )
            WtEntityList(
              v-else
              entityType="challenge"
              :data="challenges"
              :title="`Challenges (${challenges.length})`"
              to="Challenges"
              body-heading=""
            )
          WtTile(child :col="12" :is-loading="!memberProfileLoaded")
            MemberActivityOverview(:data="_.get(member, 'memberData', {})")
          WtTile(v-if="isFriendsFeatureOn" child :col="12" :is-loading="!friendsProfileLoaded" is-syncing)
            MyDashboardMyFriends(
              :data="friends"
              :username="routeUsername"
            )
          WtTile(v-if="isTeamsFeatureOn" child :col="12" :is-loading="!teamsProfileLoaded")
            EmptyEntityPlaceholder(
              v-if="_.isEmpty(teams)"
              title="Teams"
              :image="require('@/assets/placeholders/empty_team.jpg')"
              :callout="isPageLoaded(page) ? `${_.upperFirst(routeUsername)} hasn't joined a team yet.` : ''"
              textBackground
            )
            MyTeam(
              v-if="teams.length === 1"
              :teamTitle="`${_.upperFirst(routeUsername)}'s team`"
              :team="teams[0]"
            )
            WtEntityList(
              v-else
              entityType="team"
              :data="teams"
              :title="`Teams (${teams.length})`"
              to="Teams"
            )
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import EventBus from "@/eventBus";
import MemberActivityOverview from "@@/Members/MemberActivityOverview";
import TodaysProgress from "@@/Members/TodaysProgress/TodaysProgress";
import UserCard from "@@/Members/UserCard";
import WtButton from "@@/WtButton/WtButton";
import WtAddFriendButton from "@@/WtAddFriendButton/WtAddFriendButton";
import WtEntityList from "@@/WtEntityList/WtEntityList";
import WtStatRow from "@@/WtStatRow/WtStatRow";
import WtTile from "@@/WtTile/WtTile";
import WtTitle from "@@/WtTitle/WtTitle";
import StepsChart from "@@/Members/StepsChart";
import Journal from "@@/Members/Journal";
import WtFullPageLoader from "@@/WtFullPageLoader/WtFullPageLoader";
import MyTeam from "@@/Teams/MyTeam";
import MyDashboardMyFriends from "@@/Friends/MyDashboardMyFriends";
import { fetchAllMixin } from "@/mixins/fetchAllMixin";

const EmptyEntityPlaceholder = () =>
  import(
    /* webpackChunkName: "EmptyEntityPlaceholder" */ "@@/EmptyEntityPlaceholder"
  ).catch(() => {
    window.location.reload();
  });

export default {
  name: "ProfilePage",

  components: {
    EmptyEntityPlaceholder,
    MemberActivityOverview,
    TodaysProgress,
    UserCard,
    WtButton,
    WtAddFriendButton,
    WtEntityList,
    WtStatRow,
    WtTile,
    WtTitle,
    StepsChart,
    Journal,
    WtFullPageLoader,
    MyTeam,
    MyDashboardMyFriends
  },

  mixins: [fetchAllMixin],

  data() {
    return {
      page: `ProfilePage${this.$route.params.username}`,
      memberUsername: "Friend",
      memberIntIdThatWasFetched: null,
      member: {},
      stepData: [],
      journals: [],
      challenges: [],
      teams: [],
      friends: [],
      memberProfileLoaded: false,
      stepDataProfileLoaded: false,
      challengesProfileLoaded: false,
      teamsProfileLoaded: false,
      friendsProfileLoaded: false,
      usernameNotFound: false,
      memberFetchedFromURLInvite: false,
      friendsDataLoading: false,
      isFriendsFeatureOn: false
    };
  },

  computed: {
    ...mapGetters("pages", ["isPageLoaded", "isPageLoading"]),
    ...mapGetters("program", ["coreProgramMeta", "isTeamsFeatureOn"]),
    ...mapGetters("members", [
      "getMemberByName",
      "getChallengesByMemberId",
      "getTeamsByMemberId",
      "getFriendsByMemberId",
      "getStepDataByMemberId",
      "getJournalsByMemberId",
      "memberFriends",
      "friendsLoaded",
      "friendsInvitedLoaded",
      "friendsRequestingLoaded",
      "isJournalLastPage",
      "journalEntryStats",
      "journalsLoaded"
    ]),

    routeUsername() {
      return this.$route.params.username;
    },

    fullPageLoader() {
      // since there is no initial state for 'isPageLoading(this.page)'
      if (this._.isUndefined(this.isPageLoading(this.page))) {
        return true;
      }
      return this.isPageLoading(this.page);
    }
  },

  watch: {
    async routeUsername() {
      this.page = `ProfilePage${this.$route.params.username}`;
      this.resetLocalData();
      await this.toFetchOrNotToFetch(this, this.page);
      this.setLocalData();
    }
  },

  methods: {
    ...mapActions("pages", [
      "setPageLoaded",
      "setPageLoading",
      "fetchAllPageFriends"
    ]),
    ...mapActions(["snackBar"]),
    ...mapActions([
      "members/fetchMember",
      "members/fetchMemberStepData",
      "members/fetchJournals",
      "members/fetchChallenges",
      "members/fetchMemberTeams",
      "members/fetchFriends",
      "members/fetchFriendsInvited",
      "members/fetchFriendsRequesting"
    ]),

    fetchMemberJournals(
      photosOnly = false,
      friends = false,
      page = null,
      resetList = false
    ) {
      return this["members/fetchJournals"]({
        memberId: this.memberIntIdThatWasFetched,
        isProfile: true,
        photosOnly: photosOnly,
        friends: friends,
        page: page,
        resetList: resetList
      });
    },

    async fetchFriendData(vm) {
      const memberId = vm.$auth.user().id;
      // - for WtAddFriendButton
      if (!vm.isPageLoading("Friends")) {
        vm.friendsDataLoading = true;
        await Promise.all([
          // fetchFriends calls a cached endpoint
          this["members/fetchFriends"]({ memberId: memberId }),
          this["members/fetchFriendsInvited"](memberId),
          this["members/fetchFriendsRequesting"](memberId)
        ]);
        vm.friendsDataLoading = false;
      }
    },

    getAndSetMember(vm) {
      vm.memberUsername = vm.routeUsername;
      const memberObject = vm.getMemberByName(vm.memberUsername);
      vm.member = { ...memberObject }; // spread needed for reactivity to propagate
      vm.memberIntIdThatWasFetched = vm.member.id;
      vm.memberProfileLoaded = true;
      vm.isFriendsFeatureOn =
        this._.get(vm.member, "memberData.friendsViewable", "No") === "Yes";
    },

    setLocalData() {
      this.getAndSetMember(this);

      if (!this._.isEmpty(this.member) && this.member.hasOwnProperty("id")) {
        this.challenges = this.getChallengesByMemberId(this.member.id);
        this.teams = this.isTeamsFeatureOn
          ? this.getTeamsByMemberId(this.member.id)
          : null;
        this.friends = this.isFriendsFeatureOn
          ? this.getFriendsByMemberId(this.member.id)
          : null;
        this.stepData = this.getStepDataByMemberId(this.member.id);
        // either awaiting journals to load (outside Promise-All) or using already loaded journals
        this.journals = this.getJournalsByMemberId(this.member.id);
        this.stepDataProfileLoaded = true;
        this.challengesProfileLoaded = true;
        this.teamsProfileLoaded = this.isTeamsFeatureOn ? true : null;
        this.friendsProfileLoaded = this.isFriendsFeatureOn ? true : null;
      }
    },

    resetLocalData() {
      this.memberUsername = this.routeUsername;
      this.member = {};
      this.challenges = [];
      this.teams = [];
      this.friends = [];
      this.stepData = [];
      this.memberProfileLoaded = false;
      this.stepDataProfileLoaded = false;
      this.challengesProfileLoaded = false;
      this.teamsProfileLoaded = false;
    },

    async fetchAllPage(vm) {
      try {
        if (!vm.memberFetchedFromURLInvite) {
          await vm["members/fetchMember"]({
            memberId: vm.routeUsername,
            setMemberByName: true
          });

          vm.getAndSetMember(vm);
        }
        vm.memberFetchedFromURLInvite = false; // reset for all other profile views
        // ===========================================
        // First set data to global store, so we have
        // ability to quickly navigate to many
        // profile pages without reloading.
        // ===========================================
        await Promise.all([
          vm["members/fetchChallenges"](vm.memberIntIdThatWasFetched).then(
            () => {
              vm.challenges = vm.getChallengesByMemberId(
                vm.memberIntIdThatWasFetched
              );
              vm.challengesProfileLoaded = true;
            }
          ),
          vm["members/fetchMemberStepData"]({
            memberId: vm.memberIntIdThatWasFetched,
            perPage: 7,
            setById: true
          }).then(() => {
            vm.stepData = vm.getStepDataByMemberId(
              vm.memberIntIdThatWasFetched
            );
            vm.stepDataProfileLoaded = true;
          })
        ]);

        if (this.isTeamsFeatureOn) {
          vm["members/fetchMemberTeams"](vm.memberIntIdThatWasFetched).then(
            () => {
              vm.teams = vm.getTeamsByMemberId(vm.memberIntIdThatWasFetched);
              vm.teamsProfileLoaded = true;
            }
          );
        }

        if (this.isFriendsFeatureOn) {
          vm["members/fetchFriends"]({
            memberId: vm.memberIntIdThatWasFetched
          }).then(() => {
            vm.friends = vm.getFriendsByMemberId(vm.memberIntIdThatWasFetched);
            vm.friendsProfileLoaded = true;
          });
        }

        this.fetchMemberJournals(
          false,
          vm.member.id !== vm.memberIntIdThatWasFetched,
          null,
          true
        );
      } catch (error) {
        vm.usernameNotFound = true;
      }
    },

    async urlInvite(vm, invite) {
      if (invite) {
        await vm["members/fetchMember"]({
          memberId: vm.routeUsername,
          setMemberByName: true
        });

        vm.memberFetchedFromURLInvite = true;

        vm.getAndSetMember(vm);
        await vm.$nextTick(); // needed for member object to propagate to WtAddFriendButton

        // note, friend data must be loaded and v-show used for WtAddFriendButton for this to work
        EventBus.$emit("WtAddFriendButton_AcceptInvite");
      }
    }
  },

  beforeRouteEnter(to, _, next) {
    next(async vm => {
      // note, friendData needed for WtAddFriendButton, and the urlInvite method
      await vm.fetchFriendData(vm);

      // check for invite code from email
      await vm.urlInvite(vm, to.query.invite);

      await vm.toFetchOrNotToFetch(vm, `ProfilePage${vm.routeUsername}`);
      vm.setLocalData();
    });
  }
};
</script>

<style lang="sass" scoped></style>
